import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { usePageTitle } from '../AppLayout'
import { api } from '../api/client'
import type { DepotView, Holding, Money, Order, OrderStatus, Transaction }
  from '../api/client'
import { useHasPermission } from '../api/session'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Chip } from '../components/common/Chip'
import { Select } from '../components/common/Controls'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Refs, instrumentRef } from '../components/common/Refs'
import { geldtext, tag, zeitpunkt } from './priceFormat'
import { SIDE, stueckzahl } from './tradeIdeaVocabulary'
import { ORDER_STATUS, ORDER_STATUS_HELP, orderTone } from './depotVocabulary'
import {
  AusfuehrungDialog,
  DepotEinstellenDialog,
  DepotLoeschenDialog,
  StornoDialog,
  TransaktionDialog,
  TransaktionLoeschenDialog,
  ZuruecknehmenDialog,
} from './DepotDialogs'
import s from './DepotPage.module.scss'

/** Welcher Dialog offen ist. `null` heißt keiner. */
type Dialogwahl = null
  | { art: 'einstellen' }
  | { art: 'depotLoeschen' }
  | { art: 'transaktion' }
  | { art: 'bestand'; position: Holding; seite: 'BUY' | 'SELL' }
  | { art: 'ausfuehren'; order: Order }
  | { art: 'storno'; order: Order }
  | { art: 'zurueck'; order: Order }
  | { art: 'loeschen'; transaktion: Transaction }

/**
 * Ein Depot — wo aus Absicht Tatsache wird.
 *
 * Vier Karten von oben nach unten: die Kasse mit der Zahl, an der sich jede
 * Order misst (frei); die Positionen; der Kontoauszug; die Orders — offene
 * zuerst, mit den Knöpfen, die aus ihnen Tatsachen machen, die übrigen
 * Stände über den Filter. Welches Depot, sagt die Adresse; die Übersicht
 * steht unter /depots.
 *
 * ALLES GERECHNET, NICHTS GESPEICHERT: Kontostand, Reservierung, frei und
 * jede Position entstehen beim Laden aus Einlage, Transaktionen und offenen
 * Orders. Nach jedem Schreiben lädt die Seite deshalb das ganze Depot neu —
 * die Antwort des einen Aufrufs sagt nicht, was aus allem zusammen folgt.
 *
 * ORDERS ENTSTEHEN AN DER IDEE, nicht hier: Ohne Idee keine Order. Der
 * Knopf dafür steht im aufgeklappten Eintrag der Handelsideen.
 *
 * GEHANDELT WIRD AUS DEM BESTAND: Jede Position hat „Verkaufen" und
 * „Nachkaufen" — dieselbe Transaktion wie unter „Transaktion eintragen",
 * nur mit Titel, Platz, Seite und Stückzahl vorbelegt. Der Titelname ist
 * derselbe Verweis wie in den Listen: Klick zeigt die Eckdaten im Popover,
 * der Sprung auf die Stammdaten steht darin.
 */
export function DepotPage() {
  const { id } = useParams()
  const depotId = Number(id)
  const navigate = useNavigate()
  const darfLesen = useHasPermission('depot:read')
  const darfSchreiben = useHasPermission('depot:write')
  const [dialog, setDialog] = useState<Dialogwahl>(null)
  // Die Orders zeigen zuerst die offenen — das ist, was man anfasst. Die
  // uebrigen Staende ueber den Filter.
  const [historieStatus, setHistorieStatus] = useState<OrderStatus | ''>('OPEN')
  // Ein Zähler als Auslöser zum Neuladen: Nach einer Buchung ist das Depot
  // ein anderes, und die Ladefunktion soll erneut laufen. `tick` steht in
  // den Abhängigkeiten, ohne im Rumpf vorzukommen — genau das ist sein
  // Zweck: eine NEUE Funktion, damit der Effekt erneut lädt.
  const [tick, setTick] = useState(0)

  const laden = useCallback((signal: AbortSignal) => api.depot(depotId, signal),
                            [depotId, tick])
  const depot = useItemQuery<DepotView>(laden, darfLesen)

  const ladenHistorie = useCallback(
    (signal: AbortSignal) =>
      api.orders(depotId, historieStatus ? { status: historieStatus } : {},
                 signal),
    [depotId, historieStatus, tick])
  const historie = useItemQuery<Order[]>(ladenHistorie, darfLesen)

  // Der Name des Depots steht in der Kopfzeile, nicht "Depot": Wer drei
  // hat, soll sehen, in welchem er gerade bucht.
  usePageTitle(depot.item?.depot.name ?? 'Depot',
               'Kasse, Orders, Positionen und Kontoauszug')

  const fertig = () => { setDialog(null); setTick((t) => t + 1) }

  if (!darfLesen || depot.denied) {
    return <PermissionNotice permission="depot:read" subject="das Depot" />
  }
  if (depot.error) {
    return <Card span={12}><ErrorBox>{depot.error}</ErrorBox></Card>
  }
  if (depot.notFound) {
    return (
      <Card span={12}>
        <EmptyState title="kein Depot">
          Dieses Depot gibt es nicht — vielleicht wurde es gelöscht.
          Die Übersicht steht unter <Link to="/depots">Depots</Link>.
        </EmptyState>
      </Card>
    )
  }
  if (!depot.item) {
    return <Card span={12}><Loading>lädt das Depot …</Loading></Card>
  }

  const { kasse, orders, holdings, transactions } = depot.item
  // `orders` sind die offenen — hier nur noch fuer die Frage, ob das Depot leer ist.
  const kopf = depot.item.depot
  const kontostand = Number(kasse.balance.amount)
  const gebuehr = kopf.fee_per_order
  // Löschen nur, wenn nichts drin ist — der Server weist es sonst ab, und
  // der Knopf soll gar nicht erst einladen.
  const leer = transactions.length === 0 && orders.length === 0
            && (historie.item?.length ?? 1) === 0

  return (
    <>
      <Card span={12} title="Kasse"
            meta={<span className={s.meta}>
              <Link to="/depots">alle Depots</Link>
              {darfSchreiben && (
                <Button variant="plain"
                        onClick={() => setDialog({ art: 'einstellen' })}>
                  Depot einstellen
                </Button>
              )}
              {darfSchreiben && leer && (
                <Button variant="quiet"
                        onClick={() => setDialog({ art: 'depotLoeschen' })}>
                  Löschen
                </Button>
              )}
            </span>}>
        <dl className={s.kennzahlen}>
          <Kennzahl label="Einlage" wert={geldtext(kasse.cash)}
                    hinweis="Das Kapital, das grundsätzlich da ist. Nicht der Kontostand." />
          <Kennzahl label="Kontostand" wert={geldtext(kasse.balance)}
                    negativ={kontostand < 0}
                    hinweis="Einlage minus Käufe plus Verkäufe, zu den festgehaltenen Kursen des jeweiligen Tages." />
          <Kennzahl label="Reserviert" wert={geldtext(kasse.reserved)}
                    hinweis="Was offene Kauforders binden: Stück mal Limit plus Gebühr, zum jüngsten Wechselkurs." />
          <Kennzahl label="Frei" wert={geldtext(kasse.free)} stark
                    negativ={Number(kasse.free.amount) < 0}
                    hinweis="Kontostand minus Reservierung. Bis hierher darf eine Kauforder gehen." />
          <Kennzahl label="Gebühr je Order" wert={geldtext(gebuehr)}
                    hinweis="Was der Broker je Ausführung nimmt. Vorbelegt beim Buchen, mitgerechnet in der Reservierung." />
        </dl>
        <p className={s.hinweis}>
          Alle Beträge in {kopf.currency}: Limit, Kurs, Gebühr und Kasse. Was
          in dieses Depot gebucht wird, ist in {kopf.currency} gebucht — auch
          wenn der Titel woanders in einer anderen Währung notiert.
        </p>
      </Card>

      <Card span={12} title="Positionen"
            meta={<span className={s.meta}>{holdings.length || 'keine'}</span>}
            help="Je Titel und Handelsplatz, über alle Transaktionen des Depots — mit und ohne Idee. Durchschnittseinstand in der Währung des Depots, nicht FIFO; Gebühren eingerechnet.">
        {holdings.length === 0
          ? <p className={s.leer}>Keine Stücke im Depot.</p>
          : <div className={s.rollen}>
              <div className={`${s.tabelle} ${s.positionen}`}>
                <div className={s.kopf}>
                  <span>Instrument</span><span>Platz</span><span>Stück</span>
                  <span>Ø Einstand</span><span>Investiert</span>
                  <span>Realisiert</span><span>Idee</span><span />
                </div>
                {holdings.map((h) => (
                  <div key={`${h.instrument_id}-${h.exchange}`} className={s.zeile}>
                    <span className={s.text}>
                      <Refs refs={[instrumentRef(h.isin, h.name, h.instrument_id)]} leer="—" />
                    </span>
                    <span className={s.stumm}>{h.exchange}</span>
                    <span className={s.num}>{stueckzahl(h.position.quantity)}</span>
                    <span className={s.num}>
                      {h.position.average_price ? geldtext(h.position.average_price) : '—'}
                    </span>
                    <span className={s.num}>
                      {h.position.invested ? geldtext(h.position.invested) : '—'}
                    </span>
                    {h.position.realized
                      ? <Betrag wert={h.position.realized} />
                      : <span className={s.num}>—</span>}
                    <span className={s.text} title={h.headline ?? undefined}>
                      {h.headline ?? <span className={s.stumm}>ohne Idee</span>}
                    </span>
                    <span className={s.aktionen}>
                      {darfSchreiben && (
                        <>
                          <Button variant="primary"
                                  onClick={() => setDialog({ art: 'bestand', position: h, seite: 'SELL' })}>
                            Verkaufen
                          </Button>
                          <Button variant="quiet"
                                  onClick={() => setDialog({ art: 'bestand', position: h, seite: 'BUY' })}>
                            Nachkaufen
                          </Button>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>}
      </Card>

      <Card span={12} title="Kontoauszug"
            meta={darfSchreiben && (
              <Button variant="plain"
                      onClick={() => setDialog({ art: 'transaktion' })}>
                Transaktion eintragen
              </Button>
            )}
            help={<>
              Jede Transaktion, jüngste zuerst. „Kasse" ist die Bewegung in
              Depotwährung zum festgehaltenen Kurs des Tages, nach Gebühr.
              Eine Transaktion aus einer Order wird an der Order
              zurückgenommen; eine ohne Order lässt sich hier löschen.
            </>}>
        {transactions.length === 0
          ? <p className={s.leer}>
              Noch keine Transaktion. Was der Broker ausführt, wird an der
              Order gebucht; Handel ohne Order über „Transaktion eintragen".
            </p>
          : <div className={s.rollen}>
              <div className={`${s.tabelle} ${s.auszug}`}>
                <div className={s.kopf}>
                  <span>Wann</span><span>Seite</span><span>Instrument</span>
                  <span>Stück</span><span>Kurs</span><span>Gebühr</span>
                  <span>Kasse</span><span>Order</span><span>Idee</span>
                  <span>Notiz</span><span />
                </div>
                {transactions.map((t) => (
                  <div key={t.id} className={s.zeile}>
                    <span className={s.wann}>{zeitpunkt.format(new Date(t.executed_at))}</span>
                    <Seite side={t.side} />
                    <span className={s.text}>
                      <Refs refs={[instrumentRef(t.isin, t.name, t.instrument_id)]} leer="—" />
                      <span className={s.stumm}> · {t.exchange}</span>
                    </span>
                    <span className={s.num}>{stueckzahl(t.quantity)}</span>
                    <span className={s.num}>{geldtext(t.price)}</span>
                    <span className={s.num}>{geldtext(t.fee)}</span>
                    <Betrag wert={t.cash_delta} />
                    <span className={s.stumm}>{t.order_id ? `#${t.order_id}` : '—'}</span>
                    <span className={s.text} title={t.headline ?? undefined}>
                      {t.headline ?? '—'}
                    </span>
                    <span className={s.text} title={t.note ?? undefined}>{t.note ?? '—'}</span>
                    <span>
                      {darfSchreiben && t.order_id === null && (
                        <button type="button" className={s.loeschen}
                                title="Transaktion löschen"
                                aria-label="Transaktion löschen"
                                onClick={() => setDialog({ art: 'loeschen', transaktion: t })}>
                          ✕
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>}
      </Card>

      <Card span={12} title="Orders"
            meta={
              <Select value={historieStatus} aria-label="Status"
                      onChange={(event) =>
                        setHistorieStatus(event.target.value as OrderStatus | '')}>
                {Object.entries(ORDER_STATUS).map(([wert, wort]) => (
                  <option key={wert} value={wert}>{wort}</option>
                ))}
                <option value="">jeder Status</option>
              </Select>
            }
            help={<>
              <p>
                Eine Order entsteht an der Handelsidee — im aufgeklappten
                Eintrag unter „Handelsideen". Hier wird gebucht, was der
                Broker daraus gemacht hat: „Ausgeführt" legt die Transaktion
                an, „Stornieren" gibt Geld oder Stücke frei.
              </p>
              <ul>
                {Object.entries(ORDER_STATUS).map(([k, wort]) => (
                  <li key={k}>
                    <Chip tone={orderTone(k)}>{wort}</Chip> {ORDER_STATUS_HELP[k]}
                  </li>
                ))}
              </ul>
            </>}>
        {historie.loading && !historie.item && <Loading />}
        {historie.item && historie.item.length === 0 && (
          <p className={s.leer}>
            {historieStatus === 'OPEN'
              ? 'Keine offene Order. Angelegt wird sie an der Handelsidee.'
              : `Keine Order${historieStatus ? ' mit diesem Status' : ''}.`}
          </p>
        )}
        {historie.item && historie.item.length > 0 && (
          <div className={s.rollen}>
            <div className={`${s.tabelle} ${s.historie}`}>
              <div className={s.kopf}>
                <span>Angelegt</span><span>Status</span><span>Instrument</span>
                <span>Idee</span><span>Seite</span><span>Stück</span><span>Limit</span>
                <span>Gültig bis</span><span>Begründung</span><span />
              </div>
              {historie.item.map((o) => (
                <div key={o.id} className={s.zeile}>
                  <span className={s.wann}>{zeitpunkt.format(new Date(o.created))}</span>
                  <span><Chip tone={orderTone(o.status)}>{ORDER_STATUS[o.status]}</Chip></span>
                  <span className={s.text}>
                    <Refs refs={[instrumentRef(o.isin, o.name, o.instrument_id)]} leer="—" />
                  </span>
                  <span className={s.text} title={o.headline ?? undefined}>{o.headline ?? '—'}</span>
                  <Seite side={o.side} />
                  <span className={s.num}>{stueckzahl(o.quantity)}</span>
                  <span className={s.num}>{geldtext(o.limit_price)}</span>
                  <span className={s.wann}>{tag.format(new Date(o.valid_until))}</span>
                  <span className={s.text}
                        title={o.cancel_reason ? `${o.reason} — Storno: ${o.cancel_reason}` : o.reason}>
                    {o.reason}
                    {o.cancel_reason && <span className={s.stumm}> · Storno: {o.cancel_reason}</span>}
                  </span>
                  <span className={s.aktionen}>
                    {darfSchreiben && o.status === 'OPEN' && (
                      <>
                        <Button variant="primary"
                                onClick={() => setDialog({ art: 'ausfuehren', order: o })}>
                          Ausgeführt
                        </Button>
                        <Button variant="quiet"
                                onClick={() => setDialog({ art: 'storno', order: o })}>
                          Stornieren
                        </Button>
                      </>
                    )}
                    {darfSchreiben && o.status === 'FILLED' && (
                      <Button variant="quiet"
                              onClick={() => setDialog({ art: 'zurueck', order: o })}>
                        Zurücknehmen
                      </Button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/*
        Bedingt gerendert statt mit `offen`-Eigenschaft: So beginnt jeder
        Dialog leer, ohne dass jemand zurücksetzen müsste.
      */}
      {dialog?.art === 'einstellen' && (
        <DepotEinstellenDialog depot={kopf} onFertig={fertig}
                               onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'depotLoeschen' && (
        <DepotLoeschenDialog depot={kopf}
                             onFertig={() => navigate('/depots')}
                             onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'transaktion' && (
        <TransaktionDialog depotId={depotId} onFertig={fertig}
                           onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'bestand' && (
        <TransaktionDialog depotId={depotId} onFertig={fertig}
                           bestand={{
                             instrument_id: dialog.position.instrument_id,
                             isin: dialog.position.isin,
                             name: dialog.position.name,
                             exchange: dialog.position.exchange,
                             idea_id: dialog.position.idea_id,
                             quantity: dialog.position.position.quantity,
                             average_price: dialog.position.position.average_price,
                             seite: dialog.seite,
                           }}
                           onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'ausfuehren' && (
        <AusfuehrungDialog order={dialog.order} gebuehr={gebuehr}
                           onFertig={fertig}
                           onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'storno' && (
        <StornoDialog order={dialog.order} onFertig={fertig}
                      onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'zurueck' && (
        <ZuruecknehmenDialog order={dialog.order} onFertig={fertig}
                             onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'loeschen' && (
        <TransaktionLoeschenDialog transaktion={dialog.transaktion}
                                   onFertig={fertig}
                                   onAbbrechen={() => setDialog(null)} />
      )}
    </>
  )
}

function Kennzahl({ label, wert, hinweis, stark, negativ }: {
  label: string; wert: ReactNode; hinweis?: string
  stark?: boolean; negativ?: boolean
}) {
  return (
    <div className={`${s.kennzahl} ${stark ? s.stark : ''} ${negativ ? s.negativ : ''}`}
         title={hinweis}>
      <dt>{label}</dt>
      <dd>{wert}</dd>
    </div>
  )
}

function Seite({ side }: { side: 'BUY' | 'SELL' }) {
  return (
    <span className={`${s.seite} ${side === 'BUY' ? s.kauf : s.verkauf}`}>
      {SIDE[side]}
    </span>
  )
}

/** Ein Betrag mit Vorzeichen-Farbe: Zufluss grün, Abfluss rot. */
function Betrag({ wert }: { wert: Money }) {
  const n = Number(wert.amount)
  return (
    <span className={`${s.num} ${n > 0 ? s.plus : n < 0 ? s.minus : ''}`}>
      {geldtext(wert)}
    </span>
  )
}
